import { useTranslation, Trans } from 'react-i18next'
import { ReactNode, createElement } from 'react'

/** App UI language code for date pickers and similar (`th` | `en`). */
export function useLocale(): 'th' | 'en' {
  const { i18n } = useTranslation()
  const raw = i18n.resolvedLanguage ?? i18n.language ?? 'en'
  return raw.toLowerCase().startsWith('th') ? 'th' : 'en'
}

/**
 * Hook to replace next-intl's useTranslations
 * Usage: const t = useTranslations('namespace') or useTranslations()
 */
export function useTranslations(namespace?: string) {
  const { t } = useTranslation()

  const translate = (key: string, options?: Record<string, unknown>) => {
    const fullKey = namespace ? `${namespace}.${key}` : key
    return options != null ? t(fullKey, options) : t(fullKey)
  }

  // Add rich() method to support t.rich() from next-intl
  // Uses Trans component from react-i18next to handle rich text formatting
  const rich = (key: string, values?: Record<string, () => ReactNode>) => {
    const fullKey = namespace ? `${namespace}.${key}` : key
    
    if (values) {
      // Convert values object to components object for Trans
      // next-intl uses { number: () => <Component /> }
      // react-i18next Trans uses { number: <Component /> }
      const components = Object.fromEntries(
        Object.entries(values).map(([k, v]) => [k, v()])
      )
      return createElement(Trans, { i18nKey: fullKey, components })
    }
    
    return createElement(Trans, { i18nKey: fullKey })
  }

  // Return function that can be called directly or used as object
  return Object.assign(translate, {
    rich,
  }) as typeof translate & {
    (key: string, options?: Record<string, unknown>): string
    rich: (key: string, values?: Record<string, () => ReactNode>) => ReactNode
  }
}

